/**
 * Subject lookup and erasure — the operator interface for GDPR Art. 15 and Art. 17.
 *
 * The API and the cascade shipped at M2; this is the screen. It exists because a
 * compliance obligation an operator can only meet with `curl` is one that gets met late.
 *
 * **Erasure is irreversible and the screen says so before the button, not after.** The
 * confirmation asks the operator to type the identifier again — not as ceremony, but
 * because the identifier is the one thing a mis-click cannot supply.
 */

import { useState, type ReactElement } from 'react';
import { useMutation } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { Erasure, ErasureRequest, SubjectLookup, SubjectLookupRequest } from '@/lib/schemas';
import { Button, Card, ErrorState, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

const IDENTIFIER_TYPES = ['full_name', 'email', 'phone', 'username', 'url', 'orcid', 'domain'];

/** The compliance view. */
export function CompliancePage(): ReactElement {
  const canErase = useCan('execute_erasure');
  const [identifierType, setIdentifierType] = useState(IDENTIFIER_TYPES[1]!);
  const [identifierValue, setIdentifierValue] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [certificate, setCertificate] = useState<Erasure | null>(null);

  const lookup = useMutation({
    mutationFn: () =>
      request<SubjectLookup, SubjectLookupRequest>('/compliance/subject-lookup', {
        method: 'POST',
        body: { identifier_type: identifierType, identifier_value: identifierValue },
      }),
  });

  const erase = useMutation({
    mutationFn: () =>
      request<Erasure, ErasureRequest>('/compliance/erasure', {
        method: 'POST',
        body: { identifier_type: identifierType, identifier_value: identifierValue },
      }),
    onSuccess: (result) => setCertificate(result),
  });

  const confirmed = confirmation.trim() === identifierValue.trim() && identifierValue.length > 0;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Compliance</h1>

      <Card title="Subject lookup (Art. 15)">
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block text-sm">
              <span className="text-muted">Identifier type</span>
              <select
                value={identifierType}
                onChange={(event) => setIdentifierType(event.target.value)}
                className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
              >
                {IDENTIFIER_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="block flex-1 text-sm">
              <span className="text-muted">Value</span>
              <input
                value={identifierValue}
                onChange={(event) => setIdentifierValue(event.target.value)}
                className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
              />
            </label>
            <Button onClick={() => lookup.mutate()} disabled={lookup.isPending}>
              Look up
            </Button>
          </div>
          <p className="text-xs text-muted">
            The identifier is hashed before it is searched for — this platform stores digests,
            not the values themselves, so a lookup never writes the identifier anywhere.
          </p>
          {lookup.error != null && <ErrorState error={lookup.error} />}
          {lookup.data && (
            <Table
              headers={['Cases', 'Assertions', 'Evidence', 'Identifier digest']}
              rows={[
                [
                  lookup.data.case_ids.length,
                  lookup.data.assertion_count,
                  lookup.data.evidence_count,
                  <span key="h" className="break-all font-mono text-xs">
                    {lookup.data.identifier_hash}
                  </span>,
                ],
              ]}
            />
          )}
        </div>
      </Card>

      <Card title="Erasure (Art. 17)">
        {!canErase ? (
          <p className="text-sm text-muted">
            Erasure requires the <code>execute_erasure</code> permission, which your role does
            not hold.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="rounded border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              Irreversible. Evidence bodies are purged and their metadata tombstoned, so any
              report citing them stops being downloadable. A certificate is issued and the
              audit chain records what was removed.
            </p>
            <label className="block text-sm">
              <span className="text-muted">
                Type the identifier again to confirm: <code>{identifierValue || '—'}</code>
              </span>
              <input
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
              />
            </label>
            {erase.error != null && <ErrorState error={erase.error} />}
            <Button tone="danger" disabled={!confirmed || erase.isPending} onClick={() => erase.mutate()}>
              {erase.isPending ? 'Erasing…' : 'Erase this subject'}
            </Button>
            {certificate && (
              <div className="rounded border border-line bg-surface p-3 text-sm">
                <p className="font-medium">Certificate issued</p>
                <p className="mt-1 break-all font-mono text-xs">{certificate.certificate_hash}</p>
                <p className="mt-1 text-xs text-muted">
                  Request {certificate.request_id}. Keep this hash: it is what proves the
                  erasure happened.
                </p>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
